/**
 * Back Number Toggle Handler
 *
 * Controls visibility and initialization of back-number date inputs.
 */

import { BackNumberToggleForm } from "./back-number-toggle-form";
import {
    bindBackNumberToggleChange,
    bindBackNumberTransitionEnd,
    unbindBackNumberTransitionEnd,
} from "./back-number-toggle-listener";

class BackNumberToggleHandler {
    public static init(root: HTMLElement | Document | null): void {
        const domRoot = root || document;

        const toggle = BackNumberToggleForm.getToggle(domRoot);
        const dateInput = BackNumberToggleForm.getDateInput(domRoot);
        const dateContainer = BackNumberToggleForm.getDateContainer(domRoot, dateInput);

        if (!toggle || !dateInput) return;

        if (toggle.dataset.backNumberInit === "1") return;
        toggle.dataset.backNumberInit = "1";

        const collapseClass = "is-collapsed";

        const expandDateContainer = (): void => {
            if (!(dateContainer instanceof HTMLElement)) return;

            dateContainer.hidden = false;
            dateContainer.classList.add(collapseClass);
            requestAnimationFrame(() => {
                dateContainer.classList.remove(collapseClass);
            });
        };

        const collapseDateContainer = (): void => {
            if (!(dateContainer instanceof HTMLElement)) return;

            dateContainer.classList.add(collapseClass);

            const onTransitionEnd = (event: TransitionEvent): void => {
                if (event.propertyName !== "max-height") return;
                unbindBackNumberTransitionEnd(dateContainer, onTransitionEnd);
                dateContainer.hidden = true;
            };

            bindBackNumberTransitionEnd(dateContainer, onTransitionEnd);
        };

        const applyState = (initial = false): void => {
            const enabled = Boolean(toggle.checked);
            dateInput.disabled = !enabled;

            if (!(dateContainer instanceof HTMLElement)) return;

            if (enabled) {
                if (initial) {
                    dateContainer.hidden = false;
                    dateContainer.classList.remove(collapseClass);
                    return;
                }
                expandDateContainer();
                return;
            }

            if (initial) {
                dateContainer.classList.add(collapseClass);
                dateContainer.hidden = true;
                return;
            }

            collapseDateContainer();
        };

        applyState(true);
        bindBackNumberToggleChange(toggle, () => applyState(false));
    }
}

export { BackNumberToggleHandler };
